import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';
import { loadBossImageCatalog } from '../../nikke-catalog/api/nikkeCatalogApi.js';
import { applyPracticeBossImageFallback } from '../domain/raidPracticeBossImages.js';
import {
  assertRaidPracticePreviewWritable,
  isRaidPracticeMockOnlyPreview,
  raidPracticeActualRecordsForDisplay,
} from '../domain/raidPracticePreview.js';

const ERROR_MESSAGES = {
  authentication_required: '로그인 세션을 확인할 수 없습니다. 다시 로그인해주세요.',
  active_union_membership_required: '활성 유니온 가입 정보가 필요합니다.',
  verified_game_profile_required: 'BlablaLink 프로필 인증 정보가 필요합니다.',
  invalid_season_no: '레이드 차수를 확인해주세요.',
  invalid_boss_no: '보스를 다시 선택해주세요.',
  invalid_slot_no: '슬롯 번호를 확인해주세요.',
  invalid_practice_damage: '모의전 데미지는 0보다 크게 입력해주세요.',
  invalid_practice_deck: '덱은 최대 5명까지 입력할 수 있습니다.',
  practice_title_too_long: '제목은 100자 이내로 입력해주세요.',
  raid_season_not_found: '선택한 차수 설정을 찾을 수 없습니다.',
  practice_record_not_found: '모의전 기록을 찾을 수 없습니다.',
};

function requireClient() {
  if (!hasSupabaseConfig || !supabase) throw new Error('서비스 연결 설정을 확인할 수 없습니다.');
  return supabase;
}

function practiceError(error, fallback = '모의전 정보를 처리하지 못했습니다.') {
  const raw = String(error?.message || '');
  const code = Object.keys(ERROR_MESSAGES).find((key) => raw.includes(key));
  const next = new Error(ERROR_MESSAGES[code] || raw || fallback);
  next.code = code || error?.code || 'unknown_error';
  return next;
}

function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function normalizeBoss(raw = {}) {
  return {
    bossNo: numberValue(raw.boss_no),
    bossName: String(raw.boss_name || ''),
    element: String(raw.element || ''),
    imageUrl: String(raw.image_url || ''),
    comment: String(raw.boss_comment || ''),
    coefficient: numberValue(raw.coefficient, 1),
    phases: (Array.isArray(raw.phases) ? raw.phases : []).map((phase) => ({
      bossNo: numberValue(phase.boss_no),
      phaseNo: numberValue(phase.phase_no),
      maxHpEok: phase.max_hp_eok === null ? null : numberValue(phase.max_hp_eok),
      isInfinite: Boolean(phase.is_infinite),
    })),
  };
}

function normalizePractice(raw = {}) {
  return {
    id: String(raw.id || ''),
    bossNo: numberValue(raw.boss_no),
    slotNo: numberValue(raw.slot_no, 1),
    title: String(raw.title || ''),
    damageEok: numberValue(raw.damage_eok),
    deck: Array.isArray(raw.deck) ? raw.deck : [],
    used: Boolean(raw.used),
    createdAt: raw.created_at || null,
    updatedAt: raw.updated_at || null,
  };
}

function normalizeActual(raw = {}) {
  return {
    recordKey: String(raw.record_key || ''),
    memberOpenid: String(raw.member_openid || ''),
    memberNickname: String(raw.member_nickname || ''),
    bossNo: numberValue(raw.boss_no),
    phaseNo: numberValue(raw.phase_no),
    attackNo: numberValue(raw.attack_no),
    damageEok: numberValue(raw.damage_eok),
    isKill: Boolean(raw.is_kill),
    attackedAt: raw.attacked_at || null,
    deck: Array.isArray(raw.deck) ? raw.deck : [],
  };
}

function normalizeResponse(raw) {
  const selected = raw?.selected_season && typeof raw.selected_season === 'object'
    ? raw.selected_season
    : null;
  const mockOnlyPreview = isRaidPracticeMockOnlyPreview();
  const actualRecords = (Array.isArray(selected?.actual_records) ? selected.actual_records : []).map(normalizeActual);
  return {
    unionId: String(raw?.union_id || ''),
    unionName: String(raw?.union_name || ''),
    previewMode: mockOnlyPreview ? 'mock-only' : '',
    viewer: {
      memberOpenid: String(raw?.viewer?.member_openid || ''),
      nickname: String(raw?.viewer?.nickname || ''),
      role: String(raw?.viewer?.role || ''),
    },
    seasons: (Array.isArray(raw?.seasons) ? raw.seasons : []).map((season) => ({
      seasonNo: numberValue(season.season_no),
      status: String(season.status || 'draft'),
    })),
    selectedSeason: selected ? {
      id: String(selected.id || ''),
      seasonNo: numberValue(selected.season_no),
      status: String(selected.status || 'draft'),
      bosses: (Array.isArray(selected.bosses) ? selected.bosses : []).map(normalizeBoss),
      practiceRecords: (Array.isArray(selected.practice_records) ? selected.practice_records : []).map(normalizePractice),
      actualRecords: raidPracticeActualRecordsForDisplay(actualRecords),
    } : null,
  };
}

function applyBossCatalog(response, bossImageLookup) {
  if (!response?.selectedSeason || !bossImageLookup) return response;
  return {
    ...response,
    selectedSeason: {
      ...response.selectedSeason,
      bosses: applyPracticeBossImageFallback(response.selectedSeason.bosses, bossImageLookup),
    },
  };
}

export async function loadRaidPractice(seasonNo = null) {
  const requested = seasonNo === null || seasonNo === undefined || seasonNo === '' ? null : Number(seasonNo);
  const client = requireClient();
  const bossCatalogPromise = loadBossImageCatalog()
    .then((catalog) => catalog.lookup)
    .catch(() => null);
  const { data, error } = await client.rpc('get_my_raid_practice', { requested_season_no: requested });
  if (error) {
    const raw = String(error.message || '');
    if (/get_my_raid_practice|function .* does not exist|schema cache/i.test(raw)) {
      const setupError = new Error('모의전 기능 설정이 아직 적용되지 않았습니다.');
      setupError.code = 'practice_setup_required';
      throw setupError;
    }
    throw practiceError(error, '모의전 정보를 불러오지 못했습니다.');
  }
  const response = normalizeResponse(data || {});
  const bossImageLookup = await bossCatalogPromise;
  return applyBossCatalog(response, bossImageLookup);
}

export async function saveRaidPractice(payload) {
  assertRaidPracticePreviewWritable();
  const client = requireClient();
  const deck = (Array.isArray(payload.deck) ? payload.deck : [])
    .map((value) => String(value ?? '').trim())
    .filter(Boolean)
    .slice(0, 5);
  const { data, error } = await client.rpc('save_my_raid_practice', {
    target_season_no: Number(payload.seasonNo),
    target_boss_no: Number(payload.bossNo),
    target_slot_no: Number(payload.slotNo),
    target_title: String(payload.title || '').trim() || null,
    target_damage_eok: Number(payload.damageEok),
    target_deck: deck,
    target_used: Boolean(payload.used),
    target_note: null,
  });
  if (error) throw practiceError(error, '모의전 기록을 저장하지 못했습니다.');
  return data;
}

export async function deleteRaidPractice(recordId) {
  assertRaidPracticePreviewWritable();
  const client = requireClient();
  const { data, error } = await client.rpc('delete_my_raid_practice', {
    target_record_id: String(recordId || ''),
  });
  if (error) throw practiceError(error, '모의전 기록을 삭제하지 못했습니다.');
  return Boolean(data);
}
